import type { SupabaseClient } from '@supabase/supabase-js';
import { crearCreditoTrasFactura } from '../../lib/credito/crearCreditoTrasFactura';
import { estadoPagoEsPagado } from '../normalizeEstadoPago';

function numOrZero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Saldo a crédito al completar venta.
 * `monto_pendiente` can be 0 on unpaid rows — use max(explicit, total − pagado)
 * unless `estado_pago` is PAGADO.
 */
export function saldoPendienteFactura(f: {
  total?: unknown;
  montoPagado?: unknown;
  monto_pagado?: unknown;
  montoPendiente?: unknown;
  monto_pendiente?: unknown;
  saldo_pendiente?: unknown;
  estadoPago?: unknown;
  estado_pago?: unknown;
}): number {
  const total = Math.max(0, Math.round(numOrZero(f.total)));
  const pagado = Math.max(0, Math.round(numOrZero(f.montoPagado ?? f.monto_pagado)));
  const derived = Math.max(0, total - pagado);
  if (estadoPagoEsPagado(f.estadoPago ?? f.estado_pago)) return 0;

  let explicit = 0;
  const rawPend = f.montoPendiente ?? f.monto_pendiente;
  if (rawPend != null && rawPend !== '') {
    const n = Number(rawPend);
    if (Number.isFinite(n)) explicit = Math.max(0, Math.round(n));
  }
  const rawSaldo = f.saldo_pendiente;
  if (rawSaldo != null && rawSaldo !== '') {
    const n = Number(rawSaldo);
    if (Number.isFinite(n)) explicit = Math.max(explicit, Math.max(0, Math.round(n)));
  }
  return Math.max(explicit, derived);
}

export function completarVentaDebeCrearCredito(saldo: number): boolean {
  return saldo > 0;
}

function fechaLocalMasDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Tras marcar `venta_completada`, el trigger puede crear un cobro por el saldo.
 * Si hay saldo, lo pasamos a `creditos` (un solo modelo de cuenta por cobrar).
 */
export async function asegurarCreditoTrasCompletarVenta(
  supabase: SupabaseClient,
  opts: {
    empresaId: string;
    facturaId: string;
    clienteId?: string | null;
    total: number;
    montoPagado: number;
    fechaVencimientoSimple?: string | null;
  }
): Promise<'omitido' | 'cobro_convertido' | 'credito_creado'> {
  const totalC = Math.max(0, Math.round(Number(opts.total) || 0));
  const paidC = Math.max(0, Math.round(Number(opts.montoPagado) || 0));
  const saldo = Math.max(0, totalC - paidC);
  if (saldo <= 0) return 'omitido';
  const clienteId = String(opts.clienteId || '').trim();
  if (!clienteId) {
    throw new Error('Para completar con saldo pendiente el pedido necesita un cliente.');
  }

  const fv = /^\d{4}-\d{2}-\d{2}$/.test(String(opts.fechaVencimientoSimple || ''))
    ? String(opts.fechaVencimientoSimple)
    : fechaLocalMasDias(30);

  const { data: cobros, error: cobroErr } = await supabase
    .from('cobros')
    .select('id, saldo_pendiente, estado')
    .eq('empresa_id', opts.empresaId)
    .eq('factura_id', opts.facturaId);
  if (cobroErr) {
    console.warn('asegurarCreditoTrasCompletarVenta cobros:', cobroErr.message);
  }

  const cobroAbierto = (cobros || []).find((c) => {
    const est = String(c.estado || '').toUpperCase();
    if (est === 'PAGADO' || est === 'CONVERTIDO_A_CREDITO') return false;
    return Math.round(Number(c.saldo_pendiente) || 0) > 0;
  });

  if (cobroAbierto?.id) {
    const { error: rpcErr } = await supabase.rpc('convertir_cobro_a_credito', {
      p_cobro_id: cobroAbierto.id,
      p_fecha_vencimiento: fv,
      p_notas_credito: 'Saldo al completar venta.',
      p_condiciones: null,
    });
    if (rpcErr) throw new Error(rpcErr.message || 'No se pudo convertir el cobro a crédito.');
    return 'cobro_convertido';
  }

  await crearCreditoTrasFactura(supabase, {
    empresaId: opts.empresaId,
    facturaId: opts.facturaId,
    clienteId,
    total: totalC,
    montoPagado: paidC,
    config: { modo: 'simple', fechaVencimientoSimple: fv },
  });
  return 'credito_creado';
}
